/**
 * Контракт type-плагина для `<plp-field>`.
 *
 * `<plp-field>` — один WC-класс, поведение специфично для атрибута `type`.
 * Чтобы не плодить N классов, описываем поведение каждого типа как
 * `FieldType` модуль:
 *   - `render(host)` — гарантирует наличие нужного нативного input'а внутри
 *   - `getValue(host)` — текущее значение для submit
 *   - `validate(host, ctx)` — проверка перед submit
 *   - `applyState(host, state)` — например disable во время отправки
 *
 * Модули регистрируются в `register-types.ts`.
 */

import type { ValidationResult } from '../validation.js';

export interface FieldRenderContext {
  name: string;
  label?: string;
  placeholder?: string;
  required: boolean;
  defaultValue?: string;
  help?: string;
  errorMessage?: string;
  /** Атрибуты type-specific берутся непосредственно из host'а */
  host: HTMLElement;
}

export interface FieldType {
  /**
   * Срендерить (или поднять existing) внутреннюю разметку.
   * Должен быть идемпотентным — может вызываться повторно при
   * `attributeChangedCallback`.
   */
  render(ctx: FieldRenderContext): void;

  /**
   * Текущее значение поля. Для checkbox/radio может вернуть пустую строку.
   * Для multi-value (radio группы, multi-select) вернёт string или string[].
   */
  getValue(ctx: FieldRenderContext): string | string[];

  /**
   * Проверка валидности. Вызывается на submit и/или blur.
   */
  validate(ctx: FieldRenderContext): ValidationResult;

  /**
   * Должно ли поле уходить в payload даже при пустом значении.
   * Для checkbox/radio неотмеченное состояние = поле опускается.
   */
  shouldSubmit(ctx: FieldRenderContext): boolean;

  /**
   * Включить/выключить поле во время отправки формы.
   */
  setDisabled(ctx: FieldRenderContext, disabled: boolean): void;
}

/**
 * Виджет не красит поле: только семантический хук, по которому он находит свой
 * контрол, а вид пишет тот, кто пишет разметку — дизайн-ДНК или модель в
 * редакторе по ТЗ из component-docs.
 *
 * Раньше здесь стояли `rounded-lg border-gray-300 focus:border-blue-500`. Это
 * ломало дизайн-систему дважды: скруглeние не слышало ось геометрии, цвет не
 * слышал палитру, а на вопрос «почему форма синяя» модель смотрела на элемент и
 * не находила там ничего — синева жила в JS.
 */
export const INPUT_BASE_CLASS = 'plp-field-input';

export function ensureLabel(
  host: HTMLElement,
  text: string | undefined,
  requiredMark: boolean
): void {
  if (!text) {
    host.querySelector(':scope > label.plp-field-label')?.remove();
    return;
  }
  let label = host.querySelector<HTMLLabelElement>(':scope > label.plp-field-label');
  if (!label) {
    label = document.createElement('label');
    label.className = 'plp-field-label';
    host.insertBefore(label, host.firstChild);
  }
  label.textContent = text;
  if (requiredMark) {
    const star = document.createElement('span');
    star.className = 'plp-field-required';
    star.textContent = '*';
    label.appendChild(star);
  }
}

export function ensureError(host: HTMLElement): HTMLElement {
  let err = host.querySelector<HTMLElement>(':scope > .plp-field-error');
  if (!err) {
    err = document.createElement('span');
    err.className = 'plp-field-error';
    // Скрытие атрибутом, а не классом `hidden`: у страницы может не быть
    // утилитарного CSS, а сообщение об ошибке обязано быть скрыто по умолчанию.
    err.hidden = true;
    host.appendChild(err);
  }
  return err;
}

export function ensureHelp(host: HTMLElement, text: string | undefined): void {
  let help = host.querySelector<HTMLElement>(':scope > .plp-field-help');
  if (!text) {
    help?.remove();
    return;
  }
  if (!help) {
    help = document.createElement('span');
    help.className = 'plp-field-help';
    host.appendChild(help);
  }
  help.textContent = text;
}

export function showError(host: HTMLElement, message?: string): void {
  const err = ensureError(host);
  if (message) {
    err.textContent = message;
    err.hidden = false;
  } else {
    err.textContent = '';
    err.hidden = true;
  }
}
